import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import Panel from '../Panel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { VictoryAxis, VictoryChart, VictoryScatter } from 'victory';
import {
    backFillMissingPeriods,
    convertEbabPeriod,
    generateEbabPeriods
} from '../../functions/charts';
import { QuartileMarker } from './ProQuartileRank';

/**
 * Marker to represent no quartile data for a given period.
 * Rendered as svg elements with proportional dimensions that scale for any sized container.
 * @component
 * @param {Number} fontSize - The fontsize for the No Data text
 * @returns {React.ReactElement}
 */
const NoDataMarker = ({ fontSize }) => {
    const theme = useTheme();

    return (
        <>
            <text
                x={'50%'}
                y={'47%'}
                style={{
                    fill: theme.palette.common.black,
                    textAnchor: 'middle',
                    opacity: 0.3,
                    fontSize: fontSize,
                    fontWeight: 500
                }}
            >
                No
            </text>
            <text
                x={'50%'}
                y={'47%'}
                dy={fontSize}
                style={{
                    fill: theme.palette.common.black,
                    textAnchor: 'middle',
                    opacity: 0.3,
                    fontSize: fontSize,
                    fontWeight: 500
                }}
            >
                Data
            </text>
            <rect
                width='100%'
                height='100%'
                rx='11%'
                fill={theme.palette.common.black}
                fillOpacity={0.02}
            />
            <rect
                x='0.85%'
                y='0.75%'
                width='98.3%'
                height='98.5%'
                rx='10%'
                stroke={theme.palette.common.black}
                strokeWidth='1.3%'
                strokeOpacity={0.15}
                strokeLinecap='round'
                strokeDasharray='5.5%'
            />
        </>
    );
};

NoDataMarker.propTypes = {
    fontSize: PropTypes.number
};

/**
 * Displays a responsive quartile indicator that adjusts based on available data,
 * showing a Quartile Marker SVG if quartile and percentile information exists for
 * a specified ebab period; otherwise, displaying a No Data Marker SVG.
 * @component
 * @param {Number} x - x coordinate where the data point will be rendered
 * @param {Object} datum - portfolio data for the ebab period
 * @param {Boolean} isBelowMd - true if the screen size is below medium (< 900px)
 * @returns {React.ReactElement}
 */
const QuartilePoint = ({ x, datum, isBelowMd }) => {
    const fontSize = isBelowMd ? 19 : 14;
    const scaleFactor = isBelowMd ? 1.375 : 1;
    const noData = !(
        datum?.peerGroupMetrics?.percentile || datum?.peerGroupMetrics?.quartile
    );

    return (
        <svg
            x={x - scaleFactor * 41}
            y={20}
            width={scaleFactor * 82}
            height={scaleFactor * 100}
            fill='none'
            overflow='visible'
            aria-label={`${noData ? 'no data marker' : 'quartile marker'}: ${datum.ebabPeriod}`}
            data-analyticsid-={noData ? 'no-data-icon' : 'rank-icon'}
        >
            {noData ? (
                <NoDataMarker fontSize={fontSize} />
            ) : (
                <QuartileMarker
                    fontSize={fontSize}
                    percentile={datum.peerGroupMetrics.percentile}
                    quartile={datum.peerGroupMetrics.quartile}
                />
            )}
        </svg>
    );
};

QuartilePoint.propTypes = {
    x: PropTypes.number,
    datum: PropTypes.object,
    isBelowMd: PropTypes.bool
};

/**
 * Renders a responsive graph showing the selected portfolio's peer group quartile rank
 * and percentile scores over time, quarter by quarter, up to the current EBAB period.
 * Medium and above screen sizes show a rolling 3-year period, for smaller screen sizes, a
 * 2-year rolling period is shown.
 * @component
 * @param {Array<Object>} overTimeData - currently selected portfolio's time series data
 * @param {String} currentPeriod - current EBAB period
 * @returns {React.ReactElement}
 */
const ProQuartileRankOverTime = ({ overTimeData, currentPeriod }) => {
    const theme = useTheme();

    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

    const quartersToPlot = isBelowMd ? 8 : 12;

    const ebabPeriodsArray = generateEbabPeriods(
        currentPeriod || '2024-Q4',
        quartersToPlot
    );

    const dataToPlot = backFillMissingPeriods(
        ebabPeriodsArray,
        overTimeData?.filter((item) =>
            ebabPeriodsArray.includes(item.ebabPeriod)
        )
    );

    return (
        <Panel
            sx={{
                flexDirection: 'column',
                width: '100%',
                paddingLeft: isBelowMd ? '2rem' : '3rem',
                paddingRight: isBelowMd ? '2rem' : '3rem',
                paddingTop: '2.5rem',
                paddingBottom: '2.5rem'
            }}
            data-analyticsid='portfolio-drilldown-quartile-rank-ot'
        >
            <Typography variant='h2'>
                Peer Group Quartile Rank Over Time
            </Typography>
            <Typography variant='body1' color={theme.palette.font.light}>
                Rolling {isBelowMd ? 2 : 3} year period
            </Typography>
            <VictoryChart
                height={isBelowMd ? 210 : 170}
                width={1080}
                padding={{ top: 10, bottom: 35, left: 23, right: 23 }}
                domainPadding={{ x: isBelowMd ? 35 : 20 }}
            >
                <VictoryAxis
                    tickValues={ebabPeriodsArray}
                    tickFormat={(t) => convertEbabPeriod(t)}
                    style={{
                        axis: {
                            strokeWidth: 0
                        },
                        tickLabels: {
                            fontFamily: theme.typography.fontFamily,
                            fontSize: isBelowMd ? 20 : 14
                        }
                    }}
                />
                <VictoryScatter
                    data={dataToPlot}
                    dataComponent={<QuartilePoint isBelowMd={isBelowMd} />}
                    x='ebabPeriod'
                />
            </VictoryChart>
        </Panel>
    );
};

ProQuartileRankOverTime.propTypes = {
    overTimeData: PropTypes.array,
    currentPeriod: PropTypes.string
};
export default ProQuartileRankOverTime;
